<template>
    <div class="row">
        <div class="col-12 col-md-12 note-head" :class="isInlineNote && 'dentition-head'">
            <div class="note-title">
                <h4>{{ note.name }}</h4>
                <div class="dentition-search-wrapper" v-if="isInlineNote" ref="dentitionDropdownRef">
                    <div class="input-group input-group-merge">
                        <span class="input-group-text">
                            <img src="@/assets/search-icon.svg" alt="Search" width="14" height="14" />
                        </span>
                        <input
                            v-model="searchTxt"
                            @input="onSearchNote"
                            @focus="onFocusSearch"
                            @keyup.enter="onEnterSearch"
                            type="text"
                            class="form-control"
                            placeholder="Search or add new"
                        />
                    </div>
                    <ul v-if="isNoteListOpen" class="inline-note-list deep-shadow">
                        <li v-if="noteLoading && !noteResults.length" class="empty-text">Loading...</li>
                        <li
                            v-for="item in noteResults" :key="item.id"
                            @click="onSelectNoteItem(item)"
                            class="note-item"
                        >
                            <div class="form-check">
                                <input :checked="item.is_selected" class="form-check-input" type="checkbox">
                                <label :class="item.is_selected && 'text-primary text-bold'" class="form-check-label">
                                    {{ item.name }}
                                </label>
                            </div>
                        </li>
                        <li v-if="canAddNewNote" @click="onAddNewNote" class="note-item add-new-item">
                            <i class="fas fa-plus"></i>
                            Add "{{ searchTxt }}"
                        </li>
                        <li v-if="!noteLoading && !noteResults.length && !canAddNewNote" class="empty-text">No note found</li>
                    </ul>
                </div>
                <AsyncSelect
                    v-if="isInlineNote"
                    class="select-template"
                    placeholder="Select Template Name"
                    v-model="groupTemplate"
                    :additional-query="{ template_type: note.value }"
                    :api-service="fetchTemplateList"
                    @option:selected="onSelectTemplate"
                />
            </div>
            <div class="note-btn d-flex align-items-center" v-if="!note.isHideAddBtn">
                <button v-if="!isInlineNote" @click="onOpenModal(note, index)" type="button" class="btn btn-outline-primary btn-sm btn-block">
                    <i class="fas fa-plus"></i>
                </button>
            </div>
        </div>
        <div class="col-21">
            <hr class="mt-0">
        </div>
        <div class="col-12 col-md-12 note-body">
            <div class="card">
                <div class="card-body">
                    <div class="medicine" v-for="(item, noteIndex) in note.selectedNotes" :key="noteIndex">
                        <div class="medicine-name">
                             <div>
                               <span class="sr-no">{{ noteIndex + 1 }}. </span>
                               <span class="name">{{ item.name }}</span>
                             </div>
                             <div>
                               <span>{{ item.dentition ? '(' + item.dentition.join(", ").toUpperCase() + ')' : ''}}</span>
                               <span>
                                <button class="btn btn-sm" @click="onOpenDentitionModal(item, noteIndex)">
                                    <i class="fas fa-teeth"></i>
                                </button>
                            </span>
                             </div>

                        </div>
                        <div @click="onRemoveNote(item, noteIndex)" class="action" v-if="!note.isHideAddBtn">
                            <i class="far fa-times-circle close"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="col-12 note-footer pb-2" v-if="from === 'group-template'">
            <SubmitCancelBtn
                class="text-right"
                @onSubmit="onSaveTemplate"
                :loading="loading"
                :isHideCancelBtn="true"
                :disabled="note.selectedNotes.length === 0"
            />
        </div>
        <Loader v-if="loading"/>

        <AddDentitionModal
            v-if="isOpenDentitionModal"
            v-model:isOpenModal="isOpenDentitionModal"
            :dentition="dentition"
            @onSaveDentition="onSaveDentition"
            :dentitionItem="dentitionItem"
        />
    </div>
</template>

<script setup>
import { computed, inject, onMounted } from "vue";
import { ref } from "vue"
import { debounce } from 'lodash';
import useDetectOutsideClick from '@/services/useDetectOutsideClick';
import SubmitCancelBtn from '@/components/atom/SubmitCancelBtn.vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useRoute } from "vue-router";
import { convertToTitleCase } from '@/services/utils/global';
import Loader from '@/components/atom/LoaderComponent'
import AddDentitionModal from '@/components/molecule/company/dental-os/service/middle-header/AddDentitionModal.vue';
import handleHospital from '@/services/modules/hospital'

const props = defineProps({
    note: Object,
    index: {
        type: Number,
        default: -1,
    },
    from: {
        type: String,
        default: ''
    },
    dentition: {
      type: Object || null,
      default: () => {}
    },
    dentitionType: {
      type: String || null,
      default: null,
    },
})

const emit = defineEmits(['openModal'])
const input = ref(null)
const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false);
const dentitionItem = ref(null);

const isOpenDentitionModal = ref(false)

const { fetchTemplateList, fetchSingleTemplate, notesByType } = handleHospital();
const groupTemplate = ref(null);
const searchTxt = ref('');
const noteResults = ref([]);
const noteLoading = ref(false);
const isNoteListOpen = ref(false);
const dentitionDropdownRef = ref(null);

const isInlineNote = computed(() => {
    if (props.from === 'group-template') return false;
    return !props.note.isHideAddBtn;
})

const canAddNewNote = computed(() => {
    if (!searchTxt.value) return false;
    const typed = searchTxt.value.toLowerCase();
    const inResults = noteResults.value.some(function (item) {
        return item.name.toLowerCase() === typed;
    });
    if (inResults) return false;
    return !props.note.selectedNotes.some(function (item) {
        return item.name && item.name.toLowerCase() === typed;
    });
})

const isNoteSelected = (item) => {
    return props.note.selectedNotes.some(function (note) {
        return note.name === item.name;
    });
}

const getDescValue = (value) => {
    if (value === undefined) return null;
    return value;
}

const makeSelectedNote = (item) => {
    const note = {
        id: item.id,
        name: item.name,
        type: props.note.value,
        short_desc: getDescValue(item.short_desc),
        full_desc: getDescValue(item.full_desc),
        is_selected: true
    };
    if (item.dentition) {
        note.dentition = item.dentition;
    }
    return note;
}

const markSelectedNotes = (items) => {
    return items.map(function (item) {
        return {
            ...item,
            is_selected: isNoteSelected(item)
        };
    });
}

const syncSelectedNotes = () => {
    noteResults.value.forEach(function (item) {
        item.is_selected = isNoteSelected(item);
    });
}

const fetchNoteList = async () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&limit=100`;
    query += `&searchTxt=${encodeURIComponent(searchTxt.value)}`;
    noteLoading.value = true;
    await notesByType(query, props.note.value).then(res => {
        if (!res.status || !res.data) {
            noteResults.value = [];
            return;
        }
        noteResults.value = markSelectedNotes(res.data);
    }).catch(() => {
        noteResults.value = [];
    }).finally(() => {
        noteLoading.value = false;
    })
}

const onSearchNote = debounce(() => {
    isNoteListOpen.value = true;
    fetchNoteList();
}, 300)

const onFocusSearch = () => {
    isNoteListOpen.value = true;
}

const closeNoteList = () => {
    isNoteListOpen.value = false;
}

useDetectOutsideClick(dentitionDropdownRef, () => {
    closeNoteList();
})

const onSelectNoteItem = (item) => {
    const existingIndex = props.note.selectedNotes.findIndex(function (note) {
        return note.name === item.name;
    });
    if (existingIndex !== -1) {
        const removedNote = props.note.selectedNotes[existingIndex];
        if (removedNote.dentition) {
            item.dentition = removedNote.dentition;
        }
        props.note.selectedNotes.splice(existingIndex, 1);
        item.is_selected = false;
        closeNoteList();
        return;
    }
    props.note.selectedNotes.push(makeSelectedNote(item));
    item.is_selected = true;
    closeNoteList();
}

const onEnterSearch = async () => {
    if (!searchTxt.value) return;
    onSearchNote.flush();
    await fetchNoteList();
    const typed = searchTxt.value.toLowerCase();
    const matched = noteResults.value.find(function (item) {
        return item.name.toLowerCase() === typed;
    });
    if (matched) {
        onSelectNoteItem(matched);
        searchTxt.value = '';
        return;
    }
    if (!canAddNewNote.value) return;
    onAddNewNote();
}

const onAddNewNote = () => {
    const newName = searchTxt.value;
    props.note.selectedNotes.push({ name: newName });
    props.note.newNotesByDoctor.push(newName);
    searchTxt.value = '';
    closeNoteList();
}

const onSelectTemplate = async (template) => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&template_type=${props.note.value}`;
    loading.value = true;
    await fetchSingleTemplate(query, template.id).then(res => {
        if (!res.status) return showError(res.message);
        setTemplateItems(res.data);
    }).catch(err => {
        showError('Failed to load template');
    }).finally(() => {
        loading.value = false;
        groupTemplate.value = null;
    })
}

const setTemplateItems = (items) => {
    for (const item of items) {
        if (isNoteSelected(item)) continue;
        props.note.selectedNotes.push(makeSelectedNote(item));
    }
    syncSelectedNotes();
}

const onOpenModal = (note, index = -1) => {
    if(note.value){
        note.name = convertToTitleCase(note.value);
    }
    emit('openModal', {note, index})
}

const onSaveDentition = (selectedDentition) => {
    dentitionItem.value.dentition = selectedDentition;
}

const onOpenDentitionModal = (item, index) => {
    dentitionItem.value = item;
    isOpenDentitionModal.value = true
}

const getNoteLookupName = (noteName) => {
    if (noteName && noteName.name) return noteName.name;
    return noteName;
}

const onRemoveNote = (noteName, selectedIndex) => {
    props.note.selectedNotes.splice(selectedIndex, 1)
    syncSelectedNotes();

    const index = props.note.newNotesByDoctor.indexOf(getNoteLookupName(noteName));
    if(index < 0) return;

    props.note.newNotesByDoctor.splice(index, 1);
}

onMounted(() => {
    if (!isInlineNote.value) return;
    fetchNoteList();
})

</script>

<style scoped>
.note-title {
    min-width: 200px;
}
hr.mt-0 {
    display: none;
}
.note-head.dentition-head {
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 14px;
    margin-top: 0;
    margin-bottom: 0;
}
.dentition-head .note-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0 !important;
}
.dentition-head .note-title h4 {
    margin-bottom: 0;
    white-space: nowrap;
    flex: 0 0 130px;
    font-size: 14px;
    font-weight: 600;
    color: #5e5873;
}
.dentition-search-wrapper {
    position: relative;
    flex: 1 1 130px;
    min-width: 0;
}
.dentition-head .select-template {
    flex: 1 1 130px;
    min-width: 0;
}
.dentition-head .input-group {
    border-radius: 8px;
    background: #f8f8fa;
    border: 1px solid #e4e2ec;
    min-height: 38px;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.dentition-head .input-group:hover {
    border-color: #d8d6de;
}
.dentition-head .input-group:focus-within {
    background: #ffffff;
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}
.dentition-head .input-group .input-group-text {
    background: transparent;
    border: none;
    padding-right: 4px;
    opacity: 0.55;
}
.dentition-head .input-group .form-control {
    background: transparent;
    border: none;
    padding-left: 4px;
    font-size: 13px;
    box-shadow: none;
}
.dentition-head .input-group .form-control::placeholder {
    color: #b9b9c3;
}
.dentition-head .select-template :deep(.vs__dropdown-toggle) {
    border-radius: 8px;
    background: #f8f8fa;
    border: 1px solid #e4e2ec !important;
    min-height: 38px;
    padding: 3px 4px;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.dentition-head .select-template :deep(.vs__dropdown-toggle:hover) {
    border-color: #d8d6de !important;
}
.dentition-head .select-template :deep(.vs--open .vs__dropdown-toggle) {
    background: #ffffff;
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}
.dentition-head .select-template :deep(.vs__selected-options) {
    padding-left: 6px;
}
.dentition-head .select-template :deep(.vs__actions) {
    padding-right: 8px;
}
.dentition-head .select-template :deep(.vs__open-indicator) {
    fill: #6e6b7b;
    transform: scale(0.9);
}
.dentition-head .select-template :deep(.vs__search::placeholder) {
    color: #b9b9c3;
    font-size: 13px;
}
.inline-note-list {
    position: absolute;
    width: 100%;
    max-height: 240px;
    overflow-y: auto;
    padding: 4px;
    margin: 6px 0 0;
    background: #ffffff;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    box-shadow: 0 5px 20px 0 rgba(34, 41, 47, 0.12);
    list-style: none;
    z-index: 99999;
}
.note-item {
    padding: 7px 8px;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.15s ease;
    word-break: break-word;
}
.note-item:hover {
    background: #f8f8fa;
}
.note-item .form-check-label {
    cursor: pointer;
}
.add-new-item {
    color: #7367f0;
    font-weight: 500;
    border-top: 1px solid #f1f0f5;
    margin-top: 2px;
}
.empty-text {
    padding: 12px 8px;
    text-align: center;
    color: #b9b9c3;
    font-size: 13px;
}
@media (max-width: 1199px) {
    .dentition-head .note-title h4 {
        flex: 1 1 100%;
    }
    .dentition-search-wrapper,
    .dentition-head .select-template {
        flex: 1 1 100%;
    }
}
@media (max-width: 767px) {
    .note-head.dentition-head {
        padding: 10px 12px;
    }
}
.note-body .card-body{
    padding: 4px 4px 8px;
}
.note-head {
    display: flex;
    margin-top: 0;
    justify-content: space-between;
    margin-bottom: 0;
    padding: 10px 14px;
    align-items: center;
    gap: 10px;
}
.note-head h4 {
    font-size: 15px;
    font-weight: 600;
    color: #5e5873;
    margin-bottom: 0;
}
.action .close {
    color: #ea5455;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease;
}
.medicine:hover .action .close {
    opacity: 1;
}
.note-body .card {
    background: transparent;
    border: none;
    box-shadow: none;
    margin-bottom: 0;
}
.note-body .card-body:empty {
    padding: 0;
}
.medicine {
    display: flex;
    justify-content: space-between;
    margin: 0;
    padding: 6px 10px;
    border-radius: 6px;
    align-items: center;
    position: relative;
    font-size: 13px;
    color: #5e5873;
    transition: background 0.15s ease;
}
.medicine:hover {
    background: #f8f8fa;
}
.medicine .sr-no {
    color: #b9b9c3;
    font-weight: 500;
}
.medicine .medicine-name {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0;
}
</style>